import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { TbArrowLeft } from "react-icons/tb";
import { ModalCloseButton } from "../../../../Components/Molecules/ValutDialog";
import {
  PIX_DESTINATION_FORM_ID,
  PIX_TRANSFER_INFO_FORM_ID,
  PixTransferContent,
} from "./PixTransferContent";

const MODAL_PAPER_MOBILE = "!flex !flex-col !h-full !overflow-x-hidden !shadow-card app-card";
const MODAL_PAPER_BASE =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !max-h-[90vh] !w-full !max-w-[calc(100vw-2rem)] !overflow-x-hidden";
const MODAL_PAPER_CLASS = `${MODAL_PAPER_BASE} sm:!max-w-3xl`;
const MODAL_PAPER_RECEIPT_CLASS = `${MODAL_PAPER_BASE} sm:!max-w-4xl`;

export const PixTransferModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const contentRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [isConfirming, setIsConfirming] = useState(false);
  const [infoStepCanContinue, setInfoStepCanContinue] = useState(false);
  const [previewCanContinue, setPreviewCanContinue] = useState(false);
  const [isDecodingQr, setIsDecodingQr] = useState(false);
  const [destinationSubView, setDestinationSubView] = useState("destino");
  const isReceiptStep = currentStep >= 4;
  const isContactsSubView = currentStep === 0 && destinationSubView === "contacts";

  const closeWithoutRefetch = useCallback(() => onClose?.(), [onClose]);
  const closeWithRefetch = useCallback(() => onClose?.({ refetchStatement: true }), [onClose]);

  useEffect(() => {
    if (!open) {
      setCurrentStep(0);
      setIsConfirming(false);
      setInfoStepCanContinue(false);
      setPreviewCanContinue(false);
      setIsDecodingQr(false);
      setDestinationSubView("destino");
    }
  }, [open]);

  const handleStepChange = useCallback(
    ({
      step,
      isConfirming: confirming,
      infoStepCanContinue: canContinue,
      previewCanContinue: previewContinue,
      isDecodingQr: decoding,
    }) => {
      setCurrentStep(step);
      setIsConfirming(confirming);
      if (typeof canContinue === "boolean") {
        setInfoStepCanContinue(canContinue);
      }
      if (typeof previewContinue === "boolean") {
        setPreviewCanContinue(previewContinue);
      }
      if (typeof decoding === "boolean") {
        setIsDecodingQr(decoding);
      }
    },
    []
  );

  const handleDialogClose = (_, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    closeWithoutRefetch();
  };

  const handleCloseClick = () => {
    if (isReceiptStep) {
      closeWithRefetch();
      return;
    }
    closeWithoutRefetch();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      fullScreen={isMobile}
      fullWidth
      maxWidth={isReceiptStep ? "lg" : "md"}
      scroll="paper"
      PaperProps={{
        className: isMobile
          ? MODAL_PAPER_MOBILE
          : isReceiptStep
            ? MODAL_PAPER_RECEIPT_CLASS
            : MODAL_PAPER_CLASS,
      }}
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        <ModalCloseButton
          onClick={handleCloseClick}
          disabled={isConfirming || isDecodingQr}
          className="absolute right-3 top-3 z-10"
        />

        <div
          className={`min-w-0 min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-4 pb-3 pt-5 sm:px-6 ${!isMobile && !isReceiptStep ? "max-h-[70vh]" : ""}`}
        >
          <h2 className="pr-8 text-lg font-bold text-brand-dark">
            {isReceiptStep
              ? "Comprovante Pix"
              : isContactsSubView
                ? "Favorecidos"
                : "Transferir por Pix"}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            {isReceiptStep
              ? "Transferência concluída com sucesso."
              : isContactsSubView
                ? "Selecione um contato para preencher a chave Pix."
                : "Envie Pix por chave, QR Code ou copia e cola com segurança."}
          </p>

          <div className="mt-4">
            <PixTransferContent
              ref={contentRef}
              isActive={open}
              footerInModal
              onCancel={closeWithoutRefetch}
              onCloseModal={closeWithRefetch}
              onStepChange={handleStepChange}
              onDestinationSubViewChange={setDestinationSubView}
            />
          </div>
        </div>

        <div className="mt-auto shrink-0 border-t border-gray-100 px-4 pb-5 pt-4 sm:px-6">
          {currentStep === 0 && !isContactsSubView && (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="submit"
                form={PIX_DESTINATION_FORM_ID}
                className="btn-pix-primary w-full sm:w-auto"
                disabled={isDecodingQr}
              >
                {isDecodingQr ? "Decodificando…" : "Continuar →"}
              </button>
            </div>
          )}

          {currentStep === 1 && (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <button
                type="button"
                className="btn-back w-full sm:w-auto"
                onClick={() => contentRef.current?.goBack()}
              >
                <TbArrowLeft className="h-4 w-4" />
                Voltar
              </button>
              <button
                type="submit"
                form={PIX_TRANSFER_INFO_FORM_ID}
                className="btn-primary w-full sm:w-auto"
                disabled={!infoStepCanContinue}
              >
                Continuar
              </button>
            </div>
          )}

          {currentStep === 2 && (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <button
                type="button"
                className="btn-back w-full sm:w-auto"
                onClick={() => contentRef.current?.goBack()}
              >
                <TbArrowLeft className="h-4 w-4" />
                Voltar
              </button>
              <button
                type="button"
                className="btn-primary w-full sm:w-auto"
                onClick={() => contentRef.current?.submit()}
                disabled={!previewCanContinue}
              >
                Confirmar transferência
              </button>
            </div>
          )}

          {currentStep === 3 && (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <button
                type="button"
                className="btn-back w-full sm:w-auto"
                onClick={() => contentRef.current?.goBack()}
                disabled={isConfirming}
              >
                <TbArrowLeft className="h-4 w-4" />
                Voltar
              </button>
              <button
                type="button"
                className="btn-primary w-full sm:w-auto"
                onClick={() => contentRef.current?.confirmPassword()}
                disabled={isConfirming}
              >
                {isConfirming ? "Validando senha..." : "Confirmar senha"}
              </button>
            </div>
          )}

          {currentStep >= 4 && (
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              <button type="button" className="btn-outline w-full sm:w-auto" onClick={closeWithRefetch}>
                Fechar
              </button>
              <button
                type="button"
                className="btn-primary w-full sm:w-auto"
                onClick={() => contentRef.current?.reset()}
              >
                Nova transferência
              </button>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
